import { Localization } from "@viamap/viamap2-common";
import { DC } from "src/dataCollector/DataCollector";
import { PropertyInformation } from "src/managers/PropertyInformation";
import { getServitutDownloadFileName } from "src/managers/ServitutDownloadNaming";
import {
   isTingbogElectronicDocument,
   parseTinglysningsDatoToUtcMs,
   TINGBOG_SCANNED_AKT_PREFIX,
} from "src/managers/TingbogDownload";
import { getANNLLMAnalyzer } from "src/modules/ANNModule/ANNLLMAnalyzer";
import { getServitutAktFetchCandidates, getServitutAktSearchTargets } from "src/modules/ANNModule/ANNLLMAnalyzer";
import type { LLMMetadata } from "src/modules/ANNModule/ANNLLMAnalyzer";
import { isEligibleDigitalServitut } from "src/propertyInfoTemplates/TingbogServitutBatch";
import { aktIdFormat } from "src/components/DatarapportShared";
import {
   hentServitutDataOgBeliggenhed,
   type SFEColorEntry,
   type ServitutRapportBfeData,
} from "src/components/ServitutRapportData";
import { canonicalDocumentId, normalizeDatoLoebenrDigits, rowIdentity } from "../model/identity";
import type { ReportPreparedPayload } from "../store/actions";
import type { ServitutAktSearchHint, ServitutLoadProgress, ServitutMatrixColumnDetail } from "../model/report";
import { pageModelFromLists } from "../model/pages";
import { createSkeletonRow, type DocumentBinding, type PreparedAktSource, type ServitutRow, type ServitutRowHandling } from "../model/row";
import { isUsableAktPdfBlob } from "./aktPdf";

const RIGSARKIVET_TINGBOG_CUTOFF_MS = Date.UTC(1927, 3, 1, 0, 0, 0, 0);

function getServitutSummary(servitut: any): string {
   return (servitut?.tekstSummarisk || (Array.isArray(servitut?.tekster) ? servitut.tekster.map((t: any) => t?.tekst ?? t).join(", ") : "") || "").trim();
}

function getServitutDatoLoebenr(servitut: any, fallback: string): string {
   const identity = rowIdentity(servitut);
   return identity.datoloebenr || aktIdFormat(identity.aktAliasId || identity.aktId || servitut?.dokumentFilnavn || fallback);
}

function getServitutKey(servitut: any): string {
   const identity = rowIdentity(servitut);
   return identity.documentId || identity.aktAliasId || identity.aktId || servitut?.dokumentFilnavn || getServitutSummary(servitut);
}

function buildSkeletonRows(data: ServitutRapportBfeData[]): ServitutRow[] {
   const rowsByKey = new Map<string, ServitutRow>();
   data.forEach((bfeData) => {
      const bfe = Number(bfeData.BFEnummer);
      (bfeData.servitutter || []).forEach((servitut) => {
         const handling: ServitutRowHandling = isTingbogElectronicDocument(servitut)
            ? "electronicZipPackage"
            : "legacyLlmPdf";
         const id = getServitutKey(servitut);
         const existing = rowsByKey.get(id);
         if (existing) {
            if (!existing.landRegistry.includedBfes.includes(bfe)) {
               existing.landRegistry.includedBfes.push(bfe);
            }
            return;
         }
         const identity = rowIdentity(servitut);
         rowsByKey.set(id, createSkeletonRow({
            id,
            identity: {
               ...identity,
               documentId: canonicalDocumentId(servitut, id),
               datoloebenr: getServitutDatoLoebenr(servitut, id),
            },
            landRegistry: {
               servitut,
               handling,
               includedBfes: [bfe],
               displayAktId: aktIdFormat(servitut.aktId || servitut.aktAliasId || id),
               priority: `${servitut.prioritetNummer || ""}`,
               summary: getServitutSummary(servitut),
               exportFileName: getServitutDownloadFileName({
                  datoloebenr: identity.datoloebenr,
                  priorityNumber: servitut?.prioritetNummer,
                  headingText: getServitutSummary(servitut),
                  fallbackStem: identity.aktAliasId || identity.aktId || servitut?.dokumentFilnavn || "servitut",
               }),
            },
         }));
      });
   });
   return Array.from(rowsByKey.values()).sort((a, b) => {
      const bfeDiff = Math.min(...a.landRegistry.includedBfes) - Math.min(...b.landRegistry.includedBfes);
      if (bfeDiff !== 0) {
         return bfeDiff;
      }
      return (parseInt(a.landRegistry.priority) || 0) - (parseInt(b.landRegistry.priority) || 0);
   });
}

function parseDatoLoebenrToUtcMs(value?: string): number | null {
   const match = /^(\d{2})(\d{2})(\d{4})-/.exec(value?.trim() || "");
   if (!match) {
      return null;
   }
   const ms = Date.UTC(Number(match[3]), Number(match[2]) - 1, Number(match[1]), 0, 0, 0, 0);
   return Number.isNaN(ms) ? null : ms;
}

function isBeforeRigsarkivetCutoff(servitut: any, datoLoebenr?: string): boolean {
   const ms = parseTinglysningsDatoToUtcMs(servitut?.tinglysningsDato)
      ?? parseDatoLoebenrToUtcMs(rowIdentity(servitut).datoloebenr)
      ?? parseDatoLoebenrToUtcMs(datoLoebenr);
   return ms !== null && ms < RIGSARKIVET_TINGBOG_CUTOFF_MS;
}

function buildSearchFailureNote(servitut: any, datoLoebenr?: string): string {
   const attempt = getServitutAktSearchTargets(servitut);
   const tried = [attempt.korrektAktId, attempt.servitutAktId, attempt.textAktId, attempt.ovrigAktId]
      .map((value) => value?.trim())
      .filter((value, index, all): value is string => Boolean(value) && all.indexOf(value) === index);
   const searchDetail = tried.length
      ? Localization.getFormattedText("Searched akt candidates {aktIds} without finding the document.", { aktIds: tried.join(", ") })
      : Localization.getText("Neither the easement deed nor the property other deed was available to search.");
   if (isBeforeRigsarkivetCutoff(servitut, datoLoebenr)) {
      return Localization.getFormattedText(
         "Easement was not found in Tingbogen (before 1 April 1927). {searchDetail} Look in Rigsarkivet or upload the document as an annotation.",
         { searchDetail },
      );
   }
   return Localization.getFormattedText("Easement could not be found. {searchDetail}", { searchDetail });
}

async function resolvePreparedAktUrl(
   analyzer: { getAktFileUrl: (aktId: string, signal?: AbortSignal) => Promise<string> },
   aktId: string,
   signal?: AbortSignal,
): Promise<string> {
   try {
      return await analyzer.getAktFileUrl(aktId, signal);
   } catch (error) {
      if (signal?.aborted) {
         throw error;
      }
      console.warn("[Servitutredegørelse] getAktFileUrl failed, using public tingbog URL", aktId, error);
      return TINGBOG_SCANNED_AKT_PREFIX + aktId;
   }
}

export async function fetchDigitalXmlMetadata(row: ServitutRow): Promise<LLMMetadata | undefined> {
   const dokumentId = row.landRegistry.servitut?.dokumentId?.trim();
   if (!dokumentId) {
      return undefined;
   }
   try {
      const xmlDoc = await DC.TL.DokumentTingbog(dokumentId);
      const xmlText = new XMLSerializer().serializeToString(xmlDoc);
      return {
         id: row.id,
         document: xmlText,
         documentUrl: null,
         aktId: null,
         tekster: [
            ...(Array.isArray(row.landRegistry.servitut?.tekster)
               ? row.landRegistry.servitut.tekster.map((t: any) => t?.tekst ?? t).filter(Boolean)
               : []),
            row.landRegistry.summary,
         ].filter(Boolean),
         fromOvrigDataSource: null,
      };
   } catch (error) {
      console.error("[Servitutredegørelse] failed to fetch digital servitut XML", row.id, error);
      return undefined;
   }
}

function applyFailure(row: ServitutRow, pdfStatus: "missing" | "failed"): ServitutRow {
   const rigsarkivet = isBeforeRigsarkivetCutoff(row.landRegistry.servitut, row.identity.datoloebenr);
   return {
      ...row,
      pdfStatus,
      document: rigsarkivet ? { kind: "rigsarkivet" } : row.document,
      sourceMessage: buildSearchFailureNote(row.landRegistry.servitut, row.identity.datoloebenr),
   };
}

export type PreparedAktFile = { documentUrl: string; source: PreparedAktSource };

export function bindDigitalRowLocally(row: ServitutRow): ServitutRow {
   if (!isEligibleDigitalServitut(row.landRegistry.servitut)) {
      return applyFailure(row, "missing");
   }
   return {
      ...row,
      document: { kind: "tingbog_digital", electronicServitut: row.landRegistry.servitut },
      registryBinding: { kind: "tingbog_digital", electronicServitut: row.landRegistry.servitut },
      pdfStatus: "ready",
      pages: pageModelFromLists([], [], "entire_akt"),
      sourceMessage: undefined,
   };
}

async function resolveCachedAktFile(
   analyzer: { getAktFileUrl: (aktId: string, signal?: AbortSignal) => Promise<string> },
   candidate: { aktId: string; source: PreparedAktSource },
   filesByDocument: Map<string, PreparedAktFile>,
   inflightByDocument?: Map<string, Promise<PreparedAktFile>>,
   signal?: AbortSignal,
): Promise<PreparedAktFile> {
   const cached = filesByDocument.get(candidate.aktId);
   if (cached) {
      return cached;
   }
   const inflight = inflightByDocument?.get(candidate.aktId);
   if (inflight) {
      return inflight;
   }
   const pending = resolvePreparedAktUrl(analyzer, candidate.aktId, signal).then((documentUrl) => {
      const file = { documentUrl, source: candidate.source };
      filesByDocument.set(candidate.aktId, file);
      inflightByDocument?.delete(candidate.aktId);
      return file;
   }, (error) => {
      inflightByDocument?.delete(candidate.aktId);
      throw error;
   });
   inflightByDocument?.set(candidate.aktId, pending);
   return pending;
}

export async function bindRowToLandRegistry(
   row: ServitutRow,
   options: {
      stamOplysninger?: any;
      firstBfe?: ServitutRapportBfeData;
      propertyDataList?: ServitutRapportBfeData[];
      filesByDocument?: Map<string, PreparedAktFile>;
      inflightByDocument?: Map<string, Promise<PreparedAktFile>>;
      includeXmlMetadata?: boolean;
      signal?: AbortSignal;
   } = {},
): Promise<{ row: ServitutRow; metadata?: LLMMetadata }> {
   if (row.landRegistry.handling === "electronicZipPackage") {
      const next = bindDigitalRowLocally(row);
      if (next.pdfStatus !== "ready") {
         return { row: next };
      }
      return {
         row: next,
         metadata: options.includeXmlMetadata ? await fetchDigitalXmlMetadata(row) : undefined,
      };
   }

   const analyzer = getANNLLMAnalyzer();
   const candidates = getServitutAktFetchCandidates(row.landRegistry.servitut, options.stamOplysninger);
   if (!candidates.length) {
      return { row: applyFailure(row, "missing") };
   }
   const filesByDocument = options.filesByDocument ?? new Map();
   const builtMetadata = analyzer.buildMetadata(
      row.landRegistry.servitut,
      options.stamOplysninger,
      undefined,
      undefined,
      options.firstBfe ?? options.propertyDataList?.[0],
      { propertyDataList: options.propertyDataList },
   );
   for (const candidate of candidates) {
      try {
         const file = await resolveCachedAktFile(
            analyzer,
            candidate,
            filesByDocument,
            options.inflightByDocument,
            options.signal,
         );
         const document: DocumentBinding = {
            kind: "tingbog_akt",
            aktId: candidate.aktId,
            url: file.documentUrl,
            source: candidate.source,
            filename: builtMetadata.document,
         };
         return {
            row: {
               ...row,
               document,
               registryBinding: document,
               pdfStatus: "ready",
               sourceMessage: undefined,
            },
            metadata: {
               ...builtMetadata,
               id: row.id,
               documentUrl: file.documentUrl,
               fromOvrigDataSource: candidate.source === "ovrigAkt",
            },
         };
      } catch (error) {
         if (options.signal?.aborted) {
            throw error;
         }
         console.error("[Servitutredegørelse] failed to prepare akt", row.id, candidate.source, candidate.aktId, error);
      }
   }
   return { row: applyFailure(row, "failed") };
}

export async function bindRowToAkt(
   row: ServitutRow,
   params: {
      aktId: string;
      source: PreparedAktSource;
      stamOplysninger?: any;
      firstBfe?: ServitutRapportBfeData;
      propertyDataList?: ServitutRapportBfeData[];
   },
): Promise<{ row: ServitutRow; metadata?: LLMMetadata }> {
   const analyzer = getANNLLMAnalyzer();
   const blob = await analyzer.fetchPdf(params.aktId);
   if (!(await isUsableAktPdfBlob(blob))) {
      throw new Error(Localization.getText("Failed to fetch akt"));
   }
   let documentUrl: string | undefined;
   try {
      documentUrl = await analyzer.getAktFileUrl(params.aktId);
   } catch {
      documentUrl = undefined;
   }
   const builtMetadata = analyzer.buildMetadata(
      row.landRegistry.servitut,
      params.stamOplysninger,
      undefined,
      undefined,
      params.firstBfe ?? params.propertyDataList?.[0],
      { propertyDataList: params.propertyDataList },
   );
   const document: DocumentBinding = {
      kind: "tingbog_akt",
      aktId: params.aktId,
      url: documentUrl,
      source: params.source,
      filename: builtMetadata.document,
   };
   return {
      row: {
         ...row,
         document,
         registryBinding: document,
         pdfStatus: "ready",
         sourceMessage: undefined,
      },
      metadata: {
         ...builtMetadata,
         id: row.id,
         documentUrl: documentUrl || null,
         aktId: params.aktId,
         fromOvrigDataSource: params.source === "ovrigAkt",
      },
   };
}

function getSortedJordstykkerFromBfe(bfeData?: ServitutRapportBfeData): any[] {
   const sfe = bfeData?.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.find((node: any) => node.status === "Gældende")
      || bfeData?.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.[0];
   return (sfe?.jordstykkeSamlesISamletFastEjendom?.nodes || [])
      .filter((jordstykke: any) => jordstykke.status === "Gældende")
      .filter((jordstykke: any) => !sfe?.id_lokalId || jordstykke.samletFastEjendomLokalId === sfe.id_lokalId)
      .sort((a: any, b: any) => {
         const ejerlavDiff = parseInt(a.jordstykkeLiggerIEjerlav?.ejerlavskode || "0", 10)
            - parseInt(b.jordstykkeLiggerIEjerlav?.ejerlavskode || "0", 10);
         if (ejerlavDiff !== 0) {
            return ejerlavDiff;
         }
         return PropertyInformation.matrikelSortFunc(a.matrikelnummer || "", b.matrikelnummer || "");
      });
}

function buildColumnDetail(bfeData?: ServitutRapportBfeData): ServitutMatrixColumnDetail | null {
   const jordstykker = getSortedJordstykkerFromBfe(bfeData);
   const matrikler = jordstykker.map((j: any) => j.matrikelnummer).filter(Boolean);
   if (!matrikler.length) {
      return null;
   }
   const ejerlavNames = new Set<string>();
   jordstykker.forEach((j: any) => {
      const navn = j.jordstykkeLiggerIEjerlav?.ejerlavsnavn?.trim();
      if (navn) {
         ejerlavNames.add(navn);
      }
   });
   return {
      matrikler,
      ejerlav: ejerlavNames.size ? Array.from(ejerlavNames).join(", ") : undefined,
   };
}

export async function prepareServitutReport(
   colorList: SFEColorEntry[],
   options: {
      organizationId?: string;
      datoLoebenrFilter?: string;
      onProgress?: (progress: ServitutLoadProgress) => void;
   } = {},
): Promise<ReportPreparedPayload> {
   options.onProgress?.({ stage: "registry", completed: 0, total: Math.max(colorList.length, 1) });
   const data = await hentServitutDataOgBeliggenhed(colorList, (completed, total) => {
      options.onProgress?.({ stage: "registry", completed, total });
   });
   let rows = buildSkeletonRows(data);
   const filter = options.datoLoebenrFilter?.trim();
   if (filter) {
      const needle = normalizeDatoLoebenrDigits(filter);
      rows = rows.filter((row) => (
         normalizeDatoLoebenrDigits(row.identity.datoloebenr) === needle
         || normalizeDatoLoebenrDigits(row.landRegistry.displayAktId) === needle
         || normalizeDatoLoebenrDigits(row.id) === needle
      ));
   }

   const bound = rows.map((row) => (
      row.landRegistry.handling === "electronicZipPackage" ? bindDigitalRowLocally(row) : row
   ));
   options.onProgress?.({ stage: "easements", completed: bound.length, total: bound.length });

   const dataByBfe = new Map(data.map((entry) => [Number(entry.BFEnummer), entry]));
   const columnDetails: Record<string, ServitutMatrixColumnDetail> = {};
   const columnLabels: Record<string, string> = {};
   colorList.forEach((entry) => {
      const detail = buildColumnDetail(dataByBfe.get(entry.sfe));
      columnDetails[String(entry.sfe)] = detail ?? { matrikler: [`${entry.sfe}`] };
      columnLabels[entry.sfe] = detail
         ? [`${detail.matrikler[0]}${detail.matrikler.length > 1 ? " m.fl." : ""}`, detail.ejerlav].filter(Boolean).join(", ")
         : `${entry.sfe}`;
   });

   const aktSearchByBfe: Record<string, ServitutAktSearchHint> = Object.fromEntries(
      data.map((entry) => [
         String(entry.BFEnummer),
         { indskannetAktDokumentId: entry.stamOplysninger?.indskannetAktDokumentId ?? null },
      ]),
   );

   return {
      rows: bound,
      columns: colorList.map((entry) => entry.sfe),
      columnLabels,
      columnDetails,
      uniqueAktCount: countUniqueAktSources(bound, data),
      metadataById: {},
      aktSearchByBfe,
   };
}

function countUniqueAktSources(rows: ServitutRow[], data: ServitutRapportBfeData[]): number {
   const ids = new Set<string>();
   let digital = 0;
   rows.forEach((row) => {
      if (row.landRegistry.handling === "electronicZipPackage") {
         digital += 1;
         return;
      }
      const firstBfe = data.find((entry) => row.landRegistry.includedBfes.includes(Number(entry.BFEnummer)));
      getServitutAktFetchCandidates(row.landRegistry.servitut, firstBfe?.stamOplysninger).forEach((candidate) => {
         if (candidate.aktId) {
            ids.add(candidate.aktId);
         }
      });
   });
   return ids.size + digital;
}
